// WEEKLY-3 (2026-07-27) — Painel de encaminhamentos da reunião.
// IA propõe (op_reuniao_extrair_encaminhamentos) → revisão na tabela
// (aceitar/descartar, padrão ModalSugerirAtestado) → conversão em ações
// (op_reuniao_converter_encaminhamentos → operacao.atividades ou avulsas).

import { useEffect, useMemo, useState } from 'react';
import {
  Alert, App as AntApp, Button, DatePicker, Flex, Form, Input, Modal,
  Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  CheckOutlined, CloseOutlined, PlusOutlined, RobotOutlined,
  ThunderboltOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePerms } from '../../hooks/usePerms';
import { api, erroMsg, isoDia } from './utils';
import {
  Encaminhamento, ContratoOpcao, ReuniaoStatus,
  STATUS_ENCAM, PERM_GERENCIAR, PERM_CONSOLIDAR,
} from './types';

const { Text, Paragraph } = Typography;

interface Props {
  reuniaoId: string;
  statusReuniao: ReuniaoStatus;
  encaminhamentos: Encaminhamento[];
  onRecarregar: () => void;
}

export default function EncaminhamentosPanel({
  reuniaoId, statusReuniao, encaminhamentos, onRecarregar,
}: Props) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const [form] = Form.useForm();

  const [contratos, setContratos] = useState<ContratoOpcao[]>([]);
  const [carregandoContratos, setCarregandoContratos] = useState(false);
  const [extraindo, setExtraindo] = useState(false);
  const [modalNovo, setModalNovo] = useState(false);
  const [criando, setCriando] = useState(false);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [convertendo, setConvertendo] = useState(false);
  const [ultimaConversao, setUltimaConversao] = useState<number | null>(null);

  // Consolidada = somente leitura (backend também bloqueia; aqui é UX)
  const podeEditar = pode(PERM_GERENCIAR) && statusReuniao !== 'consolidada';
  const aceitos = encaminhamentos.filter(e => e.status === 'aceito');

  // Contratos pro Select — carrega UMA vez (action padrão do operacional)
  useEffect(() => {
    (async () => {
      setCarregandoContratos(true);
      try {
        const d = await api('op_listar_contratos');
        const lista = Array.isArray(d) ? d : (d?.contratos || []);
        setContratos(lista.map((c: any) => ({
          id: c.id, numero: c.numero || null, titulo: c.titulo || null,
        })));
      } catch { /* dropdown fica vazio se falhar */ }
      finally { setCarregandoContratos(false); }
    })();
  }, []);

  const contratoOpts = useMemo(() => contratos.map(c => ({
    value: c.id,
    label: `${c.numero || 's/nº'} — ${c.titulo || ''}`,
  })), [contratos]);

  // Datalist de e-mails: sugestões a partir dos responsáveis já usados
  const emailsConhecidos = useMemo(() => {
    const s = new Set<string>();
    for (const e of encaminhamentos) if (e.responsavel_email) s.add(e.responsavel_email);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [encaminhamentos]);

  async function atualizar(encamId: string, campos: Record<string, any>) {
    setSalvandoId(encamId);
    try {
      await api('op_reuniao_encam_atualizar', { id: encamId, ...campos });
      onRecarregar();
    } catch (e: any) {
      message.error('Falha ao salvar: ' + erroMsg(e));
    } finally { setSalvandoId(null); }
  }

  function salvarResponsavel(r: Encaminhamento, valor: string) {
    const t = valor.trim();
    if ((t || null) !== (r.responsavel_email || null)) {
      atualizar(r.id, { responsavel_email: t || null });
    }
  }

  async function extrair() {
    setExtraindo(true);
    try {
      const d = await api('op_reuniao_extrair_encaminhamentos', { id: reuniaoId }, 120000);
      const n = (d?.propostos || []).length;
      if (n > 0) message.success(`IA propôs ${n} encaminhamento(s) novo(s) — revise abaixo.`, 5);
      else message.info('Nada novo — os encaminhamentos da ata já estavam mapeados.');
      onRecarregar();
    } catch (e: any) {
      message.error('Falha na extração: ' + erroMsg(e));
    } finally { setExtraindo(false); }
  }

  async function criar(v: any) {
    setCriando(true);
    try {
      await api('op_reuniao_encam_criar', {
        reuniao_id: reuniaoId,
        descricao: String(v.descricao || '').trim(),
        responsavel_email: v.responsavel_email?.trim() || null,
        contrato_id: v.contrato_id || null,
        prazo: v.prazo ? v.prazo.format('YYYY-MM-DD') : null,
      });
      message.success('Encaminhamento criado.');
      setModalNovo(false);
      form.resetFields();
      onRecarregar();
    } catch (e: any) {
      message.error('Falha: ' + erroMsg(e));
    } finally { setCriando(false); }
  }

  async function converterAceitos() {
    if (!aceitos.length) return;
    setConvertendo(true);
    try {
      const d = await api('op_reuniao_converter_encaminhamentos',
        { reuniao_id: reuniaoId, ids: aceitos.map(e => e.id) }, 120000);
      const n = (d?.convertidos || []).length;
      const nErr = (d?.erros || []).length;
      setUltimaConversao(n);
      if (nErr > 0) message.warning(`${n} convertido(s), ${nErr} com erro.`, 6);
      else message.success(`${n} ação(ões) criada(s) nas atividades.`, 6);
      onRecarregar();
    } catch (e: any) {
      message.error('Falha na conversão: ' + erroMsg(e));
    } finally { setConvertendo(false); }
  }

  // Navegação padrão da casa (App.tsx escuta 'app-navigate' e roteia)
  function irParaAtividades() {
    window.dispatchEvent(new CustomEvent('app-navigate', {
      detail: { modulo: 'atividades', pagina: 'atividades' },
    }));
  }

  const columns: TableColumnsType<Encaminhamento> = [
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'descricao',
      render: (_: any, r) => (
        <div>
          {podeEditar && r.status !== 'convertido' ? (
            <Paragraph style={{ marginBottom: 0, fontSize: 12 }}
              editable={{
                tooltip: 'Editar descrição',
                onChange: (v) => {
                  const t = v.trim();
                  if (t && t !== r.descricao) atualizar(r.id, { descricao: t });
                },
              }}>
              {r.descricao}
            </Paragraph>
          ) : (
            <Text style={{ fontSize: 12 }} delete={r.status === 'descartado'}>{r.descricao}</Text>
          )}
          {r.origem === 'ia' && (
            <Tag color="purple" style={{ fontSize: 9, marginTop: 2 }}>🤖 IA</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Responsável', key: 'responsavel', width: 190,
      render: (_: any, r) => (podeEditar && r.status !== 'convertido' ? (
        <Input key={`${r.id}:${r.responsavel_email || ''}`} size="small"
          list="encam-emails" defaultValue={r.responsavel_email || ''}
          placeholder="email@…" style={{ width: 180 }}
          onBlur={(e) => salvarResponsavel(r, e.target.value)}
          onPressEnter={(e) => salvarResponsavel(r, (e.target as HTMLInputElement).value)} />
      ) : (
        <Text style={{ fontSize: 12 }}>{r.responsavel_email || '—'}</Text>
      )),
    },
    {
      title: 'Contrato', key: 'contrato', width: 220,
      render: (_: any, r) => (podeEditar && r.status !== 'convertido' ? (
        <Select size="small" allowClear showSearch optionFilterProp="label"
          placeholder="(sem contrato)" value={r.contrato_id ?? undefined}
          loading={carregandoContratos} options={contratoOpts}
          onChange={(v) => atualizar(r.id, { contrato_id: v ?? null })}
          style={{ width: 210 }} />
      ) : (
        <Text style={{ fontSize: 12 }}>{r.contrato_numero || (r.contrato_id ? '…' : '—')}</Text>
      )),
    },
    {
      title: 'Prazo', key: 'prazo', width: 140,
      render: (_: any, r) => (podeEditar && r.status !== 'convertido' ? (
        <DatePicker size="small" format="DD/MM/YYYY" placeholder="prazo"
          value={r.prazo ? dayjs(isoDia(r.prazo)) : null}
          onChange={(d) => atualizar(r.id, { prazo: d ? d.format('YYYY-MM-DD') : null })} />
      ) : (
        <Text style={{ fontSize: 12 }}>{r.prazo ? dayjs(isoDia(r.prazo)).format('DD/MM/YYYY') : '—'}</Text>
      )),
    },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_: any, r) => {
        const cfg = STATUS_ENCAM[r.status] || STATUS_ENCAM.proposto;
        return <Tag color={cfg.cor}>{cfg.label}</Tag>;
      },
    },
    {
      title: '', key: 'acoes', width: 90,
      render: (_: any, r) => {
        if (!podeEditar || r.status === 'convertido') return null;
        const ocupado = salvandoId === r.id;
        return (
          <Space size={0}>
            {r.status === 'proposto' && (
              <Tooltip title="Aceitar">
                <Button size="small" type="text" icon={<CheckOutlined />} loading={ocupado}
                  style={{ color: '#0a7f4a' }}
                  onClick={() => atualizar(r.id, { status: 'aceito' })} />
              </Tooltip>
            )}
            {(r.status === 'proposto' || r.status === 'aceito') && (
              <Tooltip title="Descartar">
                <Button size="small" type="text" danger icon={<CloseOutlined />} loading={ocupado}
                  onClick={() => atualizar(r.id, { status: 'descartado' })} />
              </Tooltip>
            )}
            {(r.status === 'aceito' || r.status === 'descartado') && (
              <Tooltip title="Voltar pra proposto">
                <Button size="small" type="text" icon={<UndoOutlined />} loading={ocupado}
                  onClick={() => atualizar(r.id, { status: 'proposto' })} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {/* datalist compartilhado pelos inputs de e-mail (linha + modal) */}
      <datalist id="encam-emails">
        {emailsConhecidos.map(e => <option key={e} value={e} />)}
      </datalist>

      <Flex gap={8} wrap="wrap" align="center" style={{ marginBottom: 10 }}>
        {podeEditar && (
          <>
            <Button icon={<RobotOutlined />} loading={extraindo} onClick={extrair}
              style={{ background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' }}>
              Extrair da ata/transcrição
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => setModalNovo(true)}>manual</Button>
          </>
        )}
        {extraindo && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            IA lendo ata + transcrição… (~20–40s)
          </Text>
        )}
      </Flex>

      <Table<Encaminhamento> rowKey="id" size="small" columns={columns}
        dataSource={encaminhamentos} pagination={false}
        scroll={{ x: 920 }}
        locale={{ emptyText: 'Nenhum encaminhamento — extraia da ata ou adicione manualmente.' }} />

      {/* Rodapé: conversão dos aceitos em ações */}
      {pode(PERM_CONSOLIDAR) && (
        <Flex justify="flex-end" style={{ marginTop: 10 }}>
          <Button type="primary" icon={<ThunderboltOutlined />} loading={convertendo}
            disabled={aceitos.length === 0} onClick={converterAceitos}>
            Converter aceitos em ações ({aceitos.length})
          </Button>
        </Flex>
      )}
      {ultimaConversao !== null && (
        <Alert type="success" showIcon closable style={{ marginTop: 8 }}
          onClose={() => setUltimaConversao(null)}
          message={
            <span>
              {ultimaConversao} ação(ões) criada(s).
              <Button type="link" size="small" onClick={irParaAtividades}
                style={{ padding: '0 4px' }}>
                Ver em Minhas Atividades →
              </Button>
            </span>
          } />
      )}

      <Modal open={modalNovo} title="Novo encaminhamento"
        onCancel={() => setModalNovo(false)} onOk={() => form.submit()}
        confirmLoading={criando} okText="Criar" destroyOnClose>
        <Form form={form} layout="vertical" onFinish={criar}>
          <Form.Item name="descricao" label="Descrição"
            rules={[{ required: true, message: 'Descreva o encaminhamento' }]}>
            <Input.TextArea rows={3} placeholder="O que ficou combinado na reunião…" />
          </Form.Item>
          <Form.Item name="responsavel_email" label="Responsável (e-mail)">
            <Input list="encam-emails" placeholder="email@hydrodataeng.com.br" />
          </Form.Item>
          <Form.Item name="contrato_id" label="Contrato">
            <Select allowClear showSearch optionFilterProp="label"
              placeholder="(sem contrato — vira ação avulsa)"
              loading={carregandoContratos} options={contratoOpts} />
          </Form.Item>
          <Form.Item name="prazo" label="Prazo">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
